import React, { useEffect, createContext, useMemo, useState, useContext, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom-v5-compat';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { messages } from './messages';
import { PagingContext } from '../../../../contexts/paging-context';
import { useQuickSearchTerm } from '../../../../hooks/routing/use-quick-search-term';
import { getExternalInventory } from '../../../../store/inventory/external-inventory.api';
import { useExternalInventoryConnectionsContext } from '../../external-inventory-connections-context';

const MIN_EXTERNAL_INVENTORY_SEARCH_LENGTH = 3;

export const ExternalInventoryContext = createContext();
export const useExternalInventoryContext = () => {
  return useContext(ExternalInventoryContext);
};

/**
 * External Inventory Context Provider
 * @param {object} props
 * @param {React.ReactNode} [props.children] - Child components
 * @returns {React.ReactElement} The context provider component
 */
export const ExternalInventoryContextProvider = ({ children }) => {
  const { page, pageSize } = useContext(PagingContext);

  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [externalInventoryData, setExternalInventoryData] = useState([]);
  const [inventoryProductIds, setInventoryProductIds] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const { connectionIds, connectionSources, hasEnabledConnections } = useExternalInventoryConnectionsContext();
  const { externalInventorySourceId } = useParams();
  const searchTerm = useQuickSearchTerm();
  const currentRequestRef = useRef();

  const refreshGridData = useCallback(() => {
    // Cancel previous request if it exists
    if (currentRequestRef.current) {
      currentRequestRef.current.cancel();
    }
    if (!connectionIds || connectionIds.length === 0) {
      return Promise.resolve();
    }

    // Skip search if search term is too short
    if (searchTerm?.length < MIN_EXTERNAL_INVENTORY_SEARCH_LENGTH || !hasEnabledConnections) {
      setExternalInventoryData([]);
      setTotalRecords(0);
      setEntitiesLoading(false);
      setInventoryProductIds([]);
      return Promise.resolve();
    }

    const externalInventorySourceIds = externalInventorySourceId ? [externalInventorySourceId] : connectionIds;

    setEntitiesLoading(true);
    const inventoryEntitiesPromise = getExternalInventory({
      connectionIds: externalInventorySourceIds,
      pageNumber: page,
      pageSize,
      searchTerm,
    });

    currentRequestRef.current = inventoryEntitiesPromise;

    inventoryEntitiesPromise.promise
      .then(({ inventory = [], totalCount, productIds }) => {
        const newInventory = inventory.map((inv) => {
          const connectionId = inv.connectionId?.toString();
          const connection = connectionSources?.find((conn) => conn?.connectionId?.toString() === connectionId);
          const connectionName = connection?.name ?? connectionId ?? '';

          return {
            ...inv,
            productId: String(inv.productId),
            connectionId,
            connectionName,
            id: `${inv.connectionSku}_${connectionId}`,
          };
        });
        setExternalInventoryData(newInventory || []);
        setTotalRecords(totalCount);
        setInventoryProductIds(productIds);
      })
      .catch(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorFetchingExternalInventoryTitle} />,
          content: <FormattedMessage {...messages.errorFetchingExternalInventoryContent} />,
        });
        setExternalInventoryData([]);
      })
      .finally(() => {
        setEntitiesLoading(false);
        currentRequestRef.current = null;
      });

    return inventoryEntitiesPromise;
  }, [connectionIds, connectionSources, externalInventorySourceId, page, pageSize, searchTerm, hasEnabledConnections]);

  const refreshRef = useRef();
  refreshRef.current = refreshGridData;

  useEffect(() => {
    refreshRef.current();

    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.cancel();
      }
    };
  }, [externalInventorySourceId, page, pageSize, connectionIds, searchTerm]);

  const value = useMemo(
    () => ({
      entitiesLoading,
      selectedProducts,
      setSelectedProducts,
      refreshGridData,
      externalInventoryData,
      setExternalInventoryData,
      totalRecords,
      inventoryProductIds,
    }),
    [
      entitiesLoading,
      selectedProducts,
      setSelectedProducts,
      refreshGridData,
      externalInventoryData,
      setExternalInventoryData,
      totalRecords,
      inventoryProductIds,
    ],
  );

  return <ExternalInventoryContext.Provider value={value}>{children}</ExternalInventoryContext.Provider>;
};
